import { Component, HostListener, OnInit, ViewChild } from '@angular/core';

import { CalendarOptions, FullCalendarComponent } from '@fullcalendar/angular';
// Bring the FullCalendar option-type augmentations this component's options
// rely on into scope: `allDaySlot` (from timegrid) and `schedulerLicenseKey`
// (from scrollgrid's premium-common). These are TYPE-ONLY imports — fully
// erased at runtime — so they don't run plugin registration; the plugins are
// actually loaded globally in staff-site.module.ts via
// FullCalendarModule.registerPlugins. (A runtime `import '@fullcalendar/...'`
// here would throw FullCalendar's "import the top-level lib first" guard in
// compilation units that don't load FullCalendarModule, e.g. the unit tests.)
import type _timeGridPluginTypes from '@fullcalendar/timegrid';
import type _scrollGridPluginTypes from '@fullcalendar/scrollgrid';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AccountService } from '../../../../../../services/account/account.service';
import {
  addFiltersToGetParams,
  capitalizeFirstLetter,
  getBrowserTimeZone,
  getTimeZoneDisplayName,
  isLightColor,
  propExists
} from '../../../../../../utils';
import { EventService } from '../../../../../../services/event/event.service';
import { CalendarEventModalComponent } from './modals/calendar-event-modal/calendar-event-modal.component';
import { LocationService } from '../../../../../../services/location/location.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import {
  loadScheduledEventFiltersFromGetParams,
  ScheduledEventFilterBookingCapacity,
  ScheduledEventFilters
} from '../../../../../../models/scheduled-event';
import { EventType, EventTypeCategory} from '../../../../../../models/event-type';
import { ScheduledEventsFiltersModalComponent } from '../events-list/modals/scheduled-events-filters-modal/scheduled-events-filters-modal.component';
import { EventPartCalendarFilters } from '../../../../../../models/event';
import { EventTypeStatus } from '../../../../../../models/event-type-enums';
import { ActivatedRoute, Router } from '@angular/router';
import { LocationSchedulerFirstDayOfWeek } from '../../../../../../models/location';


const OFFSET_FROM_TOP_PX = 152;


@Component({
  selector: 'app-event-calendar',
  templateUrl: './event-calendar.component.html',
  styleUrls: ['./event-calendar.component.css']
})
export class EventCalendarComponent implements OnInit {

  @ViewChild('calendar') calendarComponent: FullCalendarComponent;

  public filters: EventPartCalendarFilters = {}

  public eventTypes: EventType[];
  public eventCategories: EventTypeCategory[];

  public serverError: number = null;

  public calendarOptions: CalendarOptions = {
    initialView: 'timeGridWeek',
    themeSystem: 'bootstrap',
    bootstrapFontAwesome: false,
    headerToolbar: {
      start: 'today prev,next',
      center: 'title',
      end: 'dayGridMonth,timeGridWeek,timeGridDay,listDay'
    },
    buttonText: {
      today: 'Today',
      prev: 'Prev',
      next: 'Next',
      dayGridMonth: 'Month',
      timeGridWeek: 'Week',
      timeGridDay: 'Day',
      listDay: 'List'
    },
    allDaySlot: false,
    height: window.innerHeight - OFFSET_FROM_TOP_PX,
    lazyFetching: true,
    eventBackgroundColor: '#00B3A7',
    eventBorderColor: '#00B3A7',

    // For some reason we're getting the 'Your license key is invalid.' message
    // even though we aren't using any premium plugins, so put in the trial key
    // for now.
    schedulerLicenseKey: 'CC-Attribution-NonCommercial-NoDerivatives'
  };

  public calendarDataLoading = false;
  public loadingEventTypes = true;
  public loadingEventCategories = true;

  // Create instance references to enums to they may be called in the template.
  public capitalizeFirstLetter = capitalizeFirstLetter;
  public getTimeZoneDisplayName = getTimeZoneDisplayName;
  public getBrowserTimeZone = getBrowserTimeZone;

  constructor(
    public accountService: AccountService,
    public locationService: LocationService,
    private eventService: EventService,
    private eventTypeService: EventTypeService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    // Load initial query parameters from URL if present.
    this.filters = loadScheduledEventFiltersFromGetParams(this.activatedRoute);

    if (!this.activatedRoute.snapshot.queryParamMap.has('location_id')) {
      this.filters.location_id = this.accountService.getMyAccount().default_gym_id;
    }
    if (!this.activatedRoute.snapshot.queryParamMap.has('is_cancelled')) {
      this.filters.is_cancelled = false;
    }

    this.calendarOptions.events = (info, successCallback, failureCallback) => {
      this.serverError = null;

      this.filters.end__gt = info.start.toISOString();
      this.filters.start__lt = info.end.toISOString();

      this.eventService
        .getEventPartCalendar(
          1,
          // The max page size allowed by the API - about 100/day for a month view.
          3000,
          this.filters
        )
        .then(results => {
          successCallback(
            results.results.map(eventPartCalendarEntry => {
              const colorHex = eventPartCalendarEntry.calendar_color_hex;
              const colorStr = '#' + colorHex;
              let borderColor: string;
              let textColor: string;
              if (isLightColor(colorHex)) {
                borderColor = 'gray';
                textColor = 'black';
              } else {
                borderColor = colorStr;
                textColor = 'white';
              }

              return {
                id: eventPartCalendarEntry.id.toString(),
                title: (
                  eventPartCalendarEntry.event_type_name +
                  (
                    eventPartCalendarEntry.roster_name ?
                    ' - ' + eventPartCalendarEntry.roster_name :
                    ''
                  ) +
                  (
                    eventPartCalendarEntry.occurrence_name ?
                    ' - ' + eventPartCalendarEntry.occurrence_name :
                    ''
                  ) +
                  (
                    (
                      (!eventPartCalendarEntry.occurrence_name) &&
                      eventPartCalendarEntry.customer_facing_name
                    ) ?
                    ' - ' + eventPartCalendarEntry.customer_facing_name :
                    ''
                  )
                ),
                start: eventPartCalendarEntry.start_datetime,
                end: eventPartCalendarEntry.end_datetime,
                backgroundColor: colorStr,
                borderColor,
                textColor,
                extendedProps: {
                  isCancelled: eventPartCalendarEntry.is_cancelled
                }
              };
            })
          );
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.accountService.logout();
          } else {
            this.serverError = reason.status;
          }

          failureCallback(reason);
        });
    };
    this.calendarOptions.loading = (isLoading) => {
      this.calendarDataLoading = isLoading;
    };
    this.calendarOptions.eventClassNames = (arg) => {
      if (arg.event.extendedProps.isCancelled) {
        return ['event-strikethrough'];
      } else {
        return [];
      }
    };
    this.calendarOptions.eventClick = (info) => {
      const modalRef = this.modalService.open(CalendarEventModalComponent);
      modalRef.componentInstance.eventPartId = info.event.id;
    }

    switch (this.accountService.getMyAccount().default_gym_scheduler_first_day_of_week) {
      case LocationSchedulerFirstDayOfWeek.sunday:
        this.calendarOptions.firstDay = 0;
        break;
      case LocationSchedulerFirstDayOfWeek.monday:
        this.calendarOptions.firstDay = 1;
        break;
    }

    this.loadEventTypes();
    this.loadEventCategories();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.calendarOptions.height = window.innerHeight - OFFSET_FROM_TOP_PX;
  }

  public getActiveFilters(): string[] {
    const activeFilters: string[] = [];

    const fs = this.filters;

    if (this.locationService.getLocations() === null) {
      activeFilters.push('Loading...');
    } else if (this.locationService.getLocations().length > 1) {
      if (propExists(fs.location_id)) {
        const location = this.getLocationForId(fs.location_id);
        if (location !== null) {
          activeFilters.push('Event is located at ' + location.name);
        } else {
          activeFilters.push('Event is located at "[unknown location]"');
        }
      } else {
        activeFilters.push('Event is at any location')
      }
    }

    if (propExists(fs.event_type_id)) {
      if (this.loadingEventTypes) {
        activeFilters.push('Loading...');
      } else {
        const eventType = this.getEventTypeForId(fs.event_type_id);
        if (eventType !== null) {
          activeFilters.push('Event type is "' + eventType.name + '"');
        } else {
          activeFilters.push('Event type is "[unknown event type]"');
        }
      }
    }

    if (propExists(fs.event_category_id)) {
      if (this.loadingEventCategories) {
        activeFilters.push('Loading...');
      } else {
        const eventCategory = this.getEventCategoryForId(fs.event_category_id);
        if (eventCategory !== null) {
          activeFilters.push('Event category is "' + eventCategory.name + '"');
        } else {
          activeFilters.push('Event category is "[unknown event category]"');
        }
      }
    }

    if (propExists(fs.is_private)) {
      if (fs.is_private) {
        activeFilters.push('Event is private');
      } else {
        activeFilters.push('Event is not private');
      }
    }

    if (propExists(fs.is_cancelled)) {
      if (fs.is_cancelled) {
        activeFilters.push('Event is cancelled');
      } else {
        activeFilters.push('Event is not cancelled');
      }
    }

    if (propExists(fs.has_instructor)) {
      if (fs.has_instructor) {
        activeFilters.push('Event has an instructor');
      } else {
        activeFilters.push('Event does not have an instructor');
      }
    }

    if (propExists(fs.instructor_id)) {
      activeFilters.push('Event is instructed by ' + fs.instructor_name);
    }

    if (propExists(fs.booking_capacity)) {
      switch (fs.booking_capacity) {
        case ScheduledEventFilterBookingCapacity.full:
          activeFilters.push('Event is full');
          break;
        case ScheduledEventFilterBookingCapacity.notFull:
          activeFilters.push('Event is not full');
          break;
        case ScheduledEventFilterBookingCapacity.halfOrMoreBooked:
          activeFilters.push('Event is at least half booked');
          break;
        case ScheduledEventFilterBookingCapacity.lessThanHalfBooked:
          activeFilters.push('Event is less than half booked');
          break;
        case ScheduledEventFilterBookingCapacity.atLeastOneBooking:
          activeFilters.push('Event has at least one booking');
          break;
        case ScheduledEventFilterBookingCapacity.noBookings:
          activeFilters.push('Event has no bookings');
          break;
      }
    }

    if (propExists(fs.has_waitlist)) {
      if (fs.has_waitlist) {
        activeFilters.push('Event has participants on waitlist');
      } else {
        activeFilters.push('Event does not have participants on waitlist');
      }
    }

    return activeFilters;
  }

  public openEditFiltersModal() {
    if (this.loadingFiltersModalRequirements()) { return; }

    const modalRef = this.modalService.open(ScheduledEventsFiltersModalComponent);
    modalRef.componentInstance.inputFilters = this.filters;
    modalRef.componentInstance.eventTypes = this.eventTypes;
    modalRef.componentInstance.eventCategories = this.eventCategories;
    modalRef.componentInstance.fullBookingCapacityCalculation = false;
    modalRef.componentInstance.savedCallback = (newFilters: ScheduledEventFilters) => {
      this.filters.event_type_id = newFilters.event_type_id;
      this.filters.event_category_id = newFilters.event_category_id;
      this.filters.is_private = newFilters.is_private;
      this.filters.location_id = newFilters.location_id;
      this.filters.is_cancelled = newFilters.is_cancelled;
      this.filters.has_instructor = newFilters.has_instructor;
      this.filters.instructor_id = newFilters.instructor_id;
      this.filters.instructor_name = newFilters.instructor_name;
      this.filters.booking_capacity = newFilters.booking_capacity;
      this.filters.has_waitlist = newFilters.has_waitlist;

      addFiltersToGetParams(
        this.filters,
        this.router,
        this.activatedRoute,
        ['location_id'],
        [
          'start__gte',
          'start__lt',
          'end__gt',
          'end__lte',
        ]
      );

      this.calendarComponent.getApi().refetchEvents();
    };
  }

  public loadingFiltersModalRequirements() {
    return (
      this.loadingEventTypes ||
      this.loadingEventCategories ||
      (this.locationService.getLocations() === null)
    );
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(1, 3000)
      .then(response => {
        this.eventTypes = response.results
          .filter((value) => {
            return (value.status !== EventTypeStatus.draft);
          })
          .sort((a, b) => {
            if (a.status !== b.status) {
              if (a.status === EventTypeStatus.active) { return -1; }
              else { return 1; }
            }
            return (a.name > b.name) ? 1 : -1;
          });
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventTypes = false; });
  }

  private loadEventCategories() {
    this.loadingEventCategories = true;

    this.eventTypeService
      .getEventTypeCategories(1, 1000)
      .then(response => {
        this.eventCategories = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventCategories = false; });
  }

  private getEventTypeForId(eventTypeId: number): EventType | null {
    for (const eventType of this.eventTypes) {
      if (eventType.id === eventTypeId) {
        return eventType;
      }
    }

    return null;
  }

  private getEventCategoryForId(eventCategoryId: number): EventTypeCategory | null {
    for (const eventCategory of this.eventCategories) {
      if (eventCategory.id === eventCategoryId) {
        return eventCategory;
      }
    }

    return null;
  }

  private getLocationForId(locationId: number) {
    for (const location of this.locationService.getLocations()) {
      if (location.id === locationId) {
        return location;
      }
    }

    return null;
  }

}
